import { TUIMode } from '../types/model';
import { Base64 } from 'js-base64';
import { logger } from './logger';
import { nearestNumber } from './functions';

const ZOOM_FACTOR = 2;

const getStylesWithoutDefaults = (element: HTMLElement) => {
  // creating an empty dummy object to compare with
  const dummy = document.createElement('element-' + new Date().getTime());
  document.body.appendChild(dummy);

  // getting computed styles for both elements
  const defaultStyles = window.getComputedStyle(dummy);
  const elementStyles = window.getComputedStyle(element);

  // calculating the difference
  const diff = {};
  for (const key in elementStyles) {
    if (
      isNaN(+key) &&
      elementStyles.hasOwnProperty(key) &&
      defaultStyles[key] !== elementStyles[key]
    ) {
      diff[key] = elementStyles[key];
    }
  }

  // clear dom
  dummy.remove();

  return diff;
};

const isVisible = (element: HTMLElement, container: HTMLElement) => {
  const { bottom, height, top } = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return top <= containerRect.top
    ? containerRect.top - top <= height
    : bottom - containerRect.bottom <= height;
};

const syncStyle = (sourceElement: HTMLElement, targetElement: HTMLElement) => {
  const srcStyles = getStylesWithoutDefaults(sourceElement);
  // console.log('snapshot:srcStyle', srcStyles);

  // Remove class to avoid conflict
  if (sourceElement.hasAttribute('class')) {
    targetElement.removeAttribute('class');
  }

  // Save scroll positions for later reuse
  if (sourceElement.scrollTop || sourceElement.scrollLeft) {
    targetElement.dataset.scrolled = '1';
    targetElement.dataset.scrollTop = sourceElement.scrollTop + '';
    targetElement.dataset.scrollLeft = sourceElement.scrollLeft + '';
  }

  if (sourceElement.tagName === 'IMG') {
    // Force absolute path on image
    (targetElement as HTMLImageElement).src = (
      sourceElement as HTMLImageElement
    ).src;
  }

  Object.entries(srcStyles).forEach(([property, value]) => {
    try {
      targetElement.style[property] = value;
    } catch (e) {
      logger.error('snapshot:error', property, e);
    }
  });
};

const createPageContainer = (contentElement, bgColor) => {
  const body = document.createElement('body');
  const baseCss = document.createElement('link');
  baseCss.rel = 'stylesheet';
  baseCss.href =
    'https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/base.min.css';

  const fontCss = document.createElement('link');
  fontCss.rel = 'stylesheet';
  fontCss.href =
    'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&display=swap';

  const style = document.createElement('style');
  let css = `*{ font-family: 'IBM Plex Sans', sans-serif; } `;
  css += `html,body{ background: ${bgColor}; }`;
  css += `input{ background: transparent; } input::placeholder{ opacity: .5; }`;

  style.appendChild(document.createTextNode(css));

  body.appendChild(fontCss);
  body.appendChild(baseCss);
  body.appendChild(style);
  body.appendChild(contentElement);

  return body;
};

const addWatermark = (
  inlineElement: HTMLElement,
  width: number,
  height: number,
  footerHeight: number,
  bgColor: string,
  theme?: TUIMode
) => {
  const siteUrl = process.env.REACT_APP_URL;
  //
  // console.log('addWatermark', {
  //   width,
  //   height,
  //   footerHeight,
  //   bgColor,
  // });
  const containerElement = document.createElement('div');

  const contentElement = document.createElement('div');
  contentElement.style.width = width + 'px';
  contentElement.style.height = height + 'px';
  contentElement.style.background = bgColor;
  contentElement.style.display = 'flex';
  contentElement.style.alignItems = 'center';
  contentElement.style.justifyContent = 'center';
  contentElement.style.position = 'relative';

  const footerElement = document.createElement('div');
  footerElement.style.width = width + 'px';
  footerElement.style.height = footerHeight + 'px';
  footerElement.style.background = '#000000';
  footerElement.style.display = 'flex';
  footerElement.style.alignItems = 'center';
  footerElement.style.justifyContent = 'space-between';

  const logoElement = document.createElement('img');
  logoElement.src = siteUrl + '/logo-white.svg';
  logoElement.style.width = 'auto';
  logoElement.style.height = footerHeight / 3 + 'px';
  logoElement.style.margin = footerHeight / 3 + 'px';

  const timestampElement = document.createElement('div');
  timestampElement.style.color = '#FFFFFF';
  timestampElement.style.fontSize = '12px';
  timestampElement.style.marginRight = footerHeight / 3 + 'px';

  const timestamp = new Date().toISOString();
  const timestampText = document.createTextNode(
    'UTC ' +
      timestamp.substring(0, 10) +
      ' ' +
      timestamp.substring(11, 19) +
      ' - ' +
      process.env.REACT_APP_WEBSITE
  );

  const watermarkElement = document.createElement('div');
  watermarkElement.style.position = 'absolute';
  watermarkElement.style.top = '0';
  watermarkElement.style.right = '0';
  watermarkElement.style.bottom = '0';
  watermarkElement.style.left = '0';
  watermarkElement.style.display = 'flex';
  watermarkElement.style.justifyContent = 'center';
  watermarkElement.style.alignItems = 'center';

  const watermarkImg = document.createElement('img');
  watermarkImg.src =
    siteUrl + (theme === 'DARK' ? '/logo-white.svg' : '/logo-black.svg');
  watermarkImg.style.height = 'auto';
  watermarkImg.style.width = '60%';
  watermarkImg.style.opacity = '.1';

  watermarkElement.appendChild(watermarkImg);

  contentElement.appendChild(inlineElement);
  contentElement.appendChild(watermarkElement);
  footerElement.appendChild(logoElement);
  timestampElement.appendChild(timestampText);
  footerElement.appendChild(timestampElement);

  containerElement.appendChild(contentElement);
  containerElement.appendChild(footerElement);
  containerElement.style.transform = `scale(${ZOOM_FACTOR})`;
  containerElement.style.transformOrigin = 'left top';

  return containerElement;
};

const generateInlineElement = (
  sourceElement: HTMLElement,
  onlyVisible = false
) => {
  if (!sourceElement) {
    throw new Error('No element specified.');
  }

  let targetElement = sourceElement.cloneNode(true) as HTMLElement;
  targetElement.id = 'snapshot';

  syncStyle(sourceElement, targetElement);

  if (sourceElement.hasChildNodes()) {
    const sourceElementChildren = sourceElement.getElementsByTagName(
      '*'
    ) as HTMLCollectionOf<HTMLElement>;
    const targetElementChildren = targetElement.getElementsByTagName(
      '*'
    ) as HTMLCollectionOf<HTMLElement>;

    for (let i = sourceElementChildren.length; i--; ) {
      const isHidden = sourceElementChildren[i].classList.contains(
        'hidden-from-snapshot'
      );

      if (
        targetElementChildren[i].classList.contains('text-container') &&
        targetElementChildren[i]?.textContent
      ) {
        const cleanedText = targetElementChildren[i]?.textContent?.replace(
          /[^\p{L}\p{N}\p{P}\p{Z}^$\n]/gu,
          ''
        );

        if (cleanedText) {
          targetElementChildren[i].innerText = cleanedText;
        }
      }

      if (
        !isHidden &&
        (!onlyVisible ||
          isVisible(sourceElementChildren[i] as HTMLElement, sourceElement))
      ) {
        syncStyle(
          sourceElementChildren[i] as HTMLElement,
          targetElementChildren[i] as HTMLElement
        );
      } else {
        targetElementChildren[i].remove();
      }
    }
  }

  return targetElement;
};

export const extractSnapshot = (
  el,
  options: {
    w: number;
    h: number;
    fileName: string;
    theme?: TUIMode;
    preview?: true;
    watermark?: true;
  }
) => {
  logger.log('snapshot:options', options);

  const widths = [420, 860, 1300, 1740, 2180, 2620];
  const heights = [480, 980];

  const width = nearestNumber(widths, el.offsetWidth);
  const height = heights[(options?.h || 1) - 1];
  const bgColor = options.theme === 'DARK' ? '#1C1C1E' : '#FFFFFF';
  logger.log('snapshot:settings', width, height, bgColor);

  console.time('snapshot');

  let iframeWidth = width;
  let iframeHeight = height;

  const inlineElement = generateInlineElement(el);
  // logger.log('snapshot:inlineElement', inlineElement);
  // document.body.appendChild(targetElement);

  let targetElement;

  if (options?.watermark) {
    const containerPadding = 120;
    const footerHeight = 75;

    iframeWidth = width + containerPadding;
    iframeHeight = height + containerPadding + footerHeight;

    targetElement = addWatermark(
      inlineElement,
      width + containerPadding,
      height + containerPadding,
      footerHeight,
      bgColor,
      options.theme
    );
  } else {
    targetElement = inlineElement;
  }

  const pageElement = createPageContainer(targetElement, bgColor);
  // document.body.appendChild(pageElement);
  let html = pageElement.outerHTML;

  if (options?.preview) {
    const previewElement = document.createElement('div');
    const iframeElement = document.createElement('iframe');

    iframeElement.srcdoc = html;
    iframeElement.width = iframeWidth + '';
    iframeElement.height = iframeHeight + '';

    previewElement.id = 'snapshot-preview';
    previewElement.style.position = 'fixed';
    previewElement.style.top = '0px';
    previewElement.style.right = '0px';
    previewElement.style.bottom = '0px';
    previewElement.style.left = '0px';
    previewElement.style.background = 'rgba(0,0,0,.7';
    previewElement.style.zIndex = '1000';
    previewElement.style.display = 'flex';
    previewElement.style.alignItems = 'center';
    previewElement.style.justifyContent = 'center';

    previewElement.addEventListener('click', (ev) => {
      previewElement.remove();
    });

    previewElement.appendChild(iframeElement);
    document.body.appendChild(previewElement);
  }

  // logger.log('html', html);
  const b64 = Base64.encode(html);
  // logger.log('b64', b64);

  console.timeEnd('snapshot');

  return Promise.resolve({
    html,
    b64,
    width: iframeWidth * ZOOM_FACTOR,
    height: iframeHeight * ZOOM_FACTOR,
  });
};

export const downloadSnapshot = (dataUrl: string, fileName: string) => {
  const fakeLink = window.document.createElement('a');
  fakeLink.download = fileName;
  fakeLink.href = dataUrl;
  document.body.appendChild(fakeLink);
  fakeLink.click();
  document.body.removeChild(fakeLink);
  fakeLink.remove();
};
